import { Vector3 } from "@babylonjs/core/Maths";
import type { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import type { FloatArray, IndicesArray } from "@babylonjs/core/types";

export interface AmbientOcclusionOptions {
    /**
     * How far to search for neighboring vertices.
     * Smaller = faster, tighter AO.
     */
    radius?: number;

    /**
     * Dot threshold for occlusion. Lower = more sensitive.
     * Only neighbors with direction dot(normal) < threshold are counted.
     */
    neighborThreshold?: number;

    /**
     * Max amount of AO darkening. 1.0 = completely black.
     */
    maxDarken?: number;

    /**
     * Sample every Nth vertex (1 = all, 2 = every second, etc).
     * Greatly improves performance on dense meshes.
     */
    sampleStep?: number;

    /**
     * Whether to allow a vertex to contribute to its own AO.
     * Usually false.
     */
    includeSelfOcclusion?: boolean;
}

export class AmbientOcclusionHelper {
    applyGeometryBasedAO(
        vertexData: Partial<VertexData>,
        options: AmbientOcclusionOptions = {}
    ): FloatArray | undefined {
        const {
            radius = 0.4,
            neighborThreshold = 0.4,
            maxDarken = 0.8,
            sampleStep = 1,
            includeSelfOcclusion = false,
        } = options;

        const positions = vertexData.positions;
        const indices = vertexData.indices;

        if (!positions || !indices) {
            console.error('Mesh missing position or indices data for AO calculation.');
            return;
        }

        const vertexCount = positions.length / 3;
        const colors = new Float32Array(vertexCount * 4);
        const normals = this.computeVertexNormals(positions, indices);

        // Reusable vectors
        const tmpVi = new Vector3();
        const tmpVj = new Vector3();
        const tmpDir = new Vector3();

        for (let i = 0; i < vertexCount; i++) {
            tmpVi.copyFromFloats(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
            const ni = normals[i];

            let occlusion = 0;
            let neighborCount = 0;

            for (let j = 0; j < vertexCount; j += sampleStep) {
                if (!includeSelfOcclusion && i === j) continue;

                tmpVj.copyFromFloats(positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2]);

                tmpDir.copyFrom(tmpVj).subtractInPlace(tmpVi);
                const dist = tmpDir.length();

                if (dist < radius && dist > 1e-4) {
                    tmpDir.normalize();
                    const alignment = Vector3.Dot(tmpDir, ni);

                    if (alignment < neighborThreshold) {
                        occlusion += 1.0 - alignment;
                    }

                    neighborCount++;
                }
            }

            const ao = neighborCount > 0 ? occlusion / neighborCount : 0;
            const brightness = 1.0 - ao * maxDarken;

            colors[i * 4 + 0] = brightness;
            colors[i * 4 + 1] = brightness;
            colors[i * 4 + 2] = brightness;
            colors[i * 4 + 3] = 1.0;
        }

        return colors;
    }

    private computeVertexNormals(positions: FloatArray, indices: IndicesArray): Vector3[] {
        const normals = Array.from({ length: positions.length / 3 }, () => new Vector3(0, 0, 0));

        for (let i = 0; i < indices.length; i += 3) {
            const i0 = indices[i];
            const i1 = indices[i + 1];
            const i2 = indices[i + 2];

            const v0 = new Vector3(positions[i0 * 3], positions[i0 * 3 + 1], positions[i0 * 3 + 2]);
            const v1 = new Vector3(positions[i1 * 3], positions[i1 * 3 + 1], positions[i1 * 3 + 2]);
            const v2 = new Vector3(positions[i2 * 3], positions[i2 * 3 + 1], positions[i2 * 3 + 2]);

            const edge1 = v1.subtract(v0);
            const edge2 = v2.subtract(v0);
            const faceNormal = Vector3.Cross(edge1, edge2).normalize();

            normals[i0].addInPlace(faceNormal);
            normals[i1].addInPlace(faceNormal);
            normals[i2].addInPlace(faceNormal);
        }

        return normals.map(n => n.normalize());
    }
}

//     // ------------- Ray-based AO -------------

//     public async applyRayBasedAO(mesh: Mesh, scene: Scene, options: Partial<RayBasedAOOptions> = {}): Promise<void> {
//         const { maxDistance = 1, maxDarken = 0.3, sampleStep = 1, directions = this._defaultDirections } = options;

//         if (!scene) {
//             console.warn('Scene must be provided in RayBasedAO options.');
//             return;
//         }

//         const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
//         const indices = mesh.getIndices();
//         if (!positions || !indices) {
//             console.warn('Mesh missing position or indices data.');
//             return;
//         }

//         const vertexCount = positions.length / 3;
//         const colors = new Float32Array(vertexCount * 4);
//         const normals = this._computeVertexNormals(positions, indices);

//         const tmpVi = new Vector3();
//         const tmpRay = new Ray(Vector3.Zero(), Vector3.Zero());

//         for (let i = 0; i < vertexCount; i += sampleStep) {
//             tmpVi.copyFromFloats(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);

//             const normal = normals[i];
//             let occluded = 0;
//             let total = 0;

//             for (const dir of directions) {
//                 if (Vector3.Dot(dir, normal) <= 0) continue; // Only hemisphere

//                 tmpRay.origin.copyFrom(tmpVi);
//                 tmpRay.direction.copyFrom(dir);
//                 tmpRay.length = maxDistance;

//                 const hit = scene.pickWithRay(tmpRay, m => m !== mesh) as PickingInfo;

//                 if (hit?.hit && hit.pickedPoint && Vector3.Distance(tmpVi, hit.pickedPoint) < maxDistance) {
//                     occluded++;
//                 }

//                 total++;
//             }

//             const ao = total > 0 ? occluded / total : 0;
//             const brightness = 1.0 - ao * maxDarken;

//             colors[i * 4] = brightness;
//             colors[i * 4 + 1] = brightness;
//             colors[i * 4 + 2] = brightness;
//             colors[i * 4 + 3] = 1.0;
//         }

//         mesh.setVerticesData(VertexBuffer.ColorKind, colors);
//     }
